<template>
  <header>
    <nav class="relative bg-raised border-b border-rule px-4 lg:px-12 py-4">
      <div class="mx-auto max-w-screen-xl flex flex-col items-center text-center">
        <!-- The wordmark is part of the artwork, so the page carries the name
          -- as a real heading too: for search engines, for anyone using a
          -- screen reader, and for the seconds before the SVG arrives. -->
        <h1 class="sr-only">{{ t('nav.title') }}</h1>
        <a href="/" :aria-label="t('nav.title')" class="block">
          <!-- The wordmark is near-black violet and would vanish on the dark
            -- ground, so there are two lockups. A media query inside the SVG
            -- would only answer to the OS, and would be wrong the moment
            -- someone overrides it here. -->
          <img :src="theme === 'dark' ? '/logo-dark.svg' : '/logo.svg'"
               :alt="t('nav.title')"
               width="1147" height="329"
               class="h-[52px] md:h-[76px] xl:h-[92px] w-auto">
        </a>
        <!-- The install button borrows this row on a phone -- see
          -- .can-install in palette.css. visibility, not display, so the
          -- tagline keeps its space and nothing moves when the button
          -- appears after load. -->
        <p class="masthead-tagline text-base md:text-lg text-ink-mute mt-2.5">
          {{ t('nav.tagline') }}
        </p>
      </div>

      <!-- Absolute so it cannot push the centred lockup off-centre, and small
        -- enough to clear it at 320px. -->
      <!-- lang.js mounts the switcher here, the same way theme.js mounts
        -- its toggle. The control has to exist on both tiers, and this
        -- is the app tier. -->
      <!-- The tools menu, filled in by /tools.js. The same arrangement as
        -- the language switcher beside it: an empty host on every tier and one
        -- implementation that finds them all, because there are four mastheads
        -- on this site and a navigation written four times stops being the
        -- same navigation. -->
      <!-- Absolute, and on the RIGHT beside the theme toggle, for two
        -- reasons. In normal flow it landed under the centred lockup on the
        -- left, orphaned; and the panel hangs from the right edge of its host,
        -- so a host on the left put five columns off the left of the screen
        -- with only the last one showing. The offsets clear the 36px toggle at
        -- each breakpoint. -->
      <!-- Positioned in palette.css rather than here, because where it goes
        -- depends on the same breakpoint that decides whether it shows its
        -- words or a glyph, and one breakpoint in two files drifts. See
        -- .masthead-cluster-app there -- and BOTH hosts are inside it,
        -- because a host left in normal flow lands under the lockup. -->
      <!-- Desktop and tablet only, like every other tier: palette.css
        -- hides [data-youtube="head"] below 559px, which is the same
        -- breakpoint the tools host beside it switches on. -->
      <span class="masthead-cluster-app">
        <span data-youtube="head"></span>
        <span data-tools-menu></span>
      </span>

      <!-- install.js fills this only when the browser says installing is
        -- possible, and leaves it empty otherwise -- see the note in that
        -- file. Bottom left rather than the top row: both top corners are
        -- already spoken for (the language switcher left, the theme toggle
        -- right) and below 560px the tools menu takes what is left, so a
        -- fourth control up there would sit on the wordmark. Same argument
        -- the tools menu records for becoming a square on a phone. -->
      <span data-install-app data-label="Install app"
            class="install-host"></span>
      <span data-lang-switcher
            class="absolute left-3 top-3 xl:left-6"></span>

      <button type="button" @click="cycleTheme"
              class="absolute right-3 top-3 xl:right-6 flex h-9 w-9 items-center justify-center
                     rounded-[10px] border border-rule bg-raised text-ink-mute
                     transition hover:text-brand hover:border-brand
                     focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand"
              :aria-label="themeLabel" :title="themeLabel">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none"
             stroke="currentColor" stroke-width="1.7"
             stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <template v-if="pref === 'auto'">
            <path d="M3 5h18v11H3z"/><path d="M8 20h8"/><path d="M12 16v4"/>
          </template>
          <template v-else-if="pref === 'light'">
            <circle cx="12" cy="12" r="4.2"/>
            <path d="M12 2.5v2.2M12 19.3v2.2M2.5 12h2.2M19.3 12h2.2M5.1 5.1l1.6 1.6M17.3 17.3l1.6 1.6M18.9 5.1l-1.6 1.6M6.7 17.3l-1.6 1.6"/>
          </template>
          <template v-else>
            <path d="M20 14.5A8.5 8.5 0 1 1 10.2 4a6.8 6.8 0 0 0 9.8 10.5z"/>
          </template>
        </svg>
      </button>
    </nav>
  </header>
</template>

<script setup>

import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'

defineProps({
	wurflData: { type: Object, required: true },
})

const { t } = useI18n()

// The behaviour lives in public/theme.js, which runs before first paint so the
// page never flashes the wrong theme. This only mirrors its state so the icon
// and the logo stay truthful. Guarded because a missing theme.js must leave a
// working header, not a blank page.
const api = () => (typeof window !== 'undefined' ? window.imgoptTheme : null)

const pref = ref(api() ? api().pref() : 'auto')
const theme = ref(api() ? api().effective() : 'light')

const LABEL = {
	auto: 'Theme: following your system. Click for light.',
	light: 'Theme: light. Click for dark.',
	dark: 'Theme: dark. Click to follow your system.',
}
const themeLabel = computed(() => LABEL[pref.value] || LABEL.auto)

function refresh() {
	const t = api()
	if (!t) return
	pref.value = t.pref()
	theme.value = t.effective()
}

function cycleTheme() {
	const t = api()
	if (t) t.cycle()
	else refresh()
}

let stop = null
onMounted(() => {
	const t = api()
	// fires for an explicit choice and, on "auto", for the OS changing under us
	if (t) stop = t.onChange(refresh)
	refresh()
})
onBeforeUnmount(() => { if (stop) stop() })

</script>
